import { DOCUMENT, Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { TUI_IS_MOBILE, TuiPopover } from '@taiga-ui/cdk';
import { TUI_DIALOGS } from '@taiga-ui/core';
import { Observable, filter, fromEvent, isObservable, take } from 'rxjs';

import { SidePageCloseService } from '@common/components/sidepage/sidepage-close.service';
import { SidePageComponent } from '@common/components/sidepage/sidepage.component';

import { DROPDOWN_MOBILE_CLASS } from './mobile-dropdown-ghost-click.service';

type DialogEntry = TuiPopover<
  { readonly dismissible?: boolean | Observable<boolean>; readonly confirmClose?: boolean },
  unknown
>;

// Жест «назад» на телефоне закрывает верхний слой — открытый список tuiDropdownMobile,
// диалог или SidePage, как Escape, — а не уводит с маршрута. На каждый слой над страницей
// лежит своя запись истории с тем же URL: «назад» снимает её, и сервис закрывает верхний
// слой. Закрыли слой кнопкой — лишние записи убираем сами. Роутер такие переходы
// игнорирует: URL не меняется
@Injectable({ providedIn: 'root' })
export class MobileDialogBackService {
  private readonly dialogs = inject(TUI_DIALOGS);
  private readonly router = inject(Router);
  private readonly sidePageClose = inject(SidePageCloseService);
  private readonly doc = inject(DOCUMENT);
  private readonly win = this.doc.defaultView;

  // Сколько наших записей сейчас лежит в истории над страницей
  private guards = 0;
  // popstate от собственного history.go, их пропускаем
  private skipped = 0;
  // Открыт полноэкранный список tuiDropdownMobile — он над всеми диалогами
  private dropdownOpen = false;

  constructor() {
    if (!inject(TUI_IS_MOBILE) || !this.win) return;

    this.dialogs.subscribe(() => this.sync());

    // Taiga отмечает открытый список классом на body
    new MutationObserver(() => {
      const open = this.doc.body.classList.contains(DROPDOWN_MOBILE_CLASS);

      if (open !== this.dropdownOpen) {
        this.dropdownOpen = open;
        this.sync();
      }
    }).observe(this.doc.body, { attributes: true, attributeFilter: ['class'] });

    fromEvent<PopStateEvent>(this.win, 'popstate').subscribe(() => this.onPopState());

    // Переход по ссылке из диалога кладёт запись поверх наших — снимать их уже нельзя,
    // иначе history.go откатит этот переход
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart =>
            event instanceof NavigationStart && event.navigationTrigger !== 'popstate',
        ),
      )
      .subscribe(() => (this.guards = 0));
  }

  private sync(): void {
    const history = this.win?.history;
    if (!history) return;

    const count = this.dialogs.value.length + Number(this.dropdownOpen);

    while (this.guards < count) {
      history.pushState(history.state, '');
      this.guards++;
    }

    if (this.guards > count) {
      const extra = this.guards - count;
      this.guards = count;
      this.skipped++;
      history.go(-extra);
    }
  }

  private onPopState(): void {
    if (this.skipped) {
      this.skipped--;
      return;
    }

    if (!this.guards) return;

    this.guards--;

    if (this.dropdownOpen) {
      // Список закрывается, когда фокус уходит из поля
      (this.doc.activeElement as HTMLElement | null)?.blur();
    } else {
      const top = this.dialogs.value.at(-1) as DialogEntry | undefined;
      if (top) this.close(top);
    }

    // Слой не закрылся (dismissible: false) — возвращаем запись, иначе следующий
    // «назад» уведёт со страницы при открытом диалоге
    setTimeout(() => this.sync());
  }

  private close(entry: DialogEntry): void {
    // SidePage закрывается как по своему крестику — формы сначала спрашивают подтверждение
    if (entry.component.component === SidePageComponent) {
      this.sidePageClose.requestClose(entry);
      return;
    }

    // tui-dialog — как по Escape: только если dismissible
    const { dismissible = true } = entry;

    if (isObservable(dismissible)) {
      dismissible.pipe(take(1)).subscribe((allowed) => allowed && entry.$implicit.complete());
    } else if (dismissible) {
      entry.$implicit.complete();
    }
  }
}
